import { publicApi } from './public-api';
import { resolveLocale, translate } from './i18n';

/**
 * Deep-link auto-restore on bundle boot.
 *
 * Legacy contract (`?dl=<uuid>` in the URL): when the merchant's page
 * loads with a saved-configuration share token as query param, the
 * bundle fetches the asset by id and hydrates the configurator with
 * it. Mail-link flows depend on this — a merchant emails
 * `https://shop/product.html?dl=<uuid>` to a customer, the customer
 * clicks, the product page loads and the configurator opens with the
 * saved state already applied.
 *
 * V3 implementation: after `Configento.ready` resolves (all mounted
 * instances have their live API attached), read the `dl` param from
 * `window.location.search`, call `Configento.loadAsset(id)` to fetch
 * the payload, and hand it to `Configento.first().restoreFromAssetJson(...)`.
 *
 * Failure mode is intentionally quiet: the page still renders a
 * working (empty) configurator on any error (missing asset, network,
 * bad payload). Only a `console.warn` gets logged — no user-facing
 * banner. The share-URL feature is a nice-to-have; a broken link
 * should not break the storefront.
 *
 * The param name matches the legacy convention (`dl`) so existing
 * mail archives keep working after the V3 rollout without any URL
 * rewriting on the merchant's side. Legacy alias `configento_asset`
 * is also honoured for forward-compat with any host that already
 * migrated to a more descriptive name.
 */
const DL_PARAM_NAMES = ['dl', 'configento_asset'] as const;

export function autoRestoreFromDeepLink(): void {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    let id: string | null = null;
    for (const name of DL_PARAM_NAMES) {
        const v = params.get(name);
        if (v && v.trim().length > 0) {
            id = v.trim();
            break;
        }
    }
    if (id === null) return;

    // Wait for ready — bundle mounts need their live API attached before
    // we can call restoreFromAssetJson on them.
    publicApi.ready
        .then(async () => {
            const inst = publicApi.first();
            if (!inst) {
                console.warn(`[Configento deep-link] ?dl=${id} present but no configurator is mounted on this page.`);
                return;
            }
            let payload: unknown;
            try {
                payload = await publicApi.loadAsset(id!);
            } catch (err) {
                console.warn(`[Configento deep-link] load failed for id=${id}:`, err);
                return;
            }
            try {
                inst.restoreFromAssetJson(payload as Parameters<typeof inst.restoreFromAssetJson>[0]);
            } catch (err) {
                console.warn(`[Configento deep-link] restore failed for id=${id}:`, err);
            }
        })
        .catch((err) => {
            console.warn('[Configento deep-link] Configento.ready rejected:', err);
        });
}

/**
 * Merchant tool, same as legacy: with `?tb=1` in the page URL a small bar
 * offers "Generate deep link". It stores the current configuration
 * (`storeAsset()`) and shows the page URL with `?dl=<id>`, copied to the
 * clipboard — the link that reopens exactly this configuration.
 */
export function renderDeepLinkToolbar(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('tb')) return;

    publicApi.ready.then(() => {
        const inst = publicApi.first();
        if (!inst || document.getElementById('cfg-deeplink-toolbar')) return;
        const locale = resolveLocale(document.documentElement.lang || undefined);

        const bar = document.createElement('div');
        bar.id = 'cfg-deeplink-toolbar';
        bar.className = 'cfg-deeplink-toolbar';
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = translate('deeplink.create', locale);
        const out = document.createElement('input');
        out.type = 'text';
        out.readOnly = true;
        out.className = 'cfg-deeplink-toolbar__url';
        out.hidden = true;
        const note = document.createElement('span');
        note.className = 'cfg-deeplink-toolbar__note';
        bar.append(button, out, note);
        document.body.prepend(bar);

        button.addEventListener('click', async () => {
            button.disabled = true;
            note.textContent = '';
            try {
                const stored = await inst.storeAsset();
                const url = new URL(window.location.href);
                url.searchParams.delete('tb');
                url.searchParams.set('dl', stored.id);
                out.value = url.toString();
                out.hidden = false;
                out.select();
                try {
                    await navigator.clipboard.writeText(out.value);
                    note.textContent = translate('deeplink.copied', locale);
                } catch {
                    note.textContent = translate('deeplink.created', locale);
                }
            } catch (err) {
                console.warn('[Configento deep-link] could not store the configuration:', err);
                note.textContent = translate('deeplink.failed', locale);
            } finally {
                button.disabled = false;
            }
        });
    }).catch(() => { /* no configurator on the page */ });
}
